import { isEmpty } from 'lodash';
import { useMemo } from 'react';
import { DropTarget } from '@shared/components/DropTarget';
import { FileList } from '@shared/components/FileList';
import { ImageList } from '@shared/components/ImageList';
import { View } from '@shared/components/View';
import { MBFileDropListener } from '@shared/components/form/fileDropListener';
import { MBTouchableOverlay } from '@shared/components/touchable';
import { FileUpload } from '@shared/types/fieldValues';
import { UseFileUploadOptions } from '@shared/util/hooks';
import { isImage } from '@shared/util/media/type';

interface Props extends Pick<UseFileUploadOptions, 'acceptedContentTypes' | 'onFileRemoved'> {
  placeholder?: string;
  files: FileUpload[];
  error?: boolean;
  mode?: 'lean' | 'full';
  isReadOnly?: boolean;
  handleDrop: (files: File[]) => void;
  showFilePicker: () => void;
  uploading: boolean;
}

export function FileInput({
  files,
  placeholder,
  error,
  acceptedContentTypes,
  onFileRemoved,
  handleDrop,
  showFilePicker,
  uploading,
  mode = 'full',
  isReadOnly = false,
}: Props) {
  const images = useMemo(() => files.filter(isImage), [files]);
  const nonImages = useMemo(() => files.filter(file => !isImage(file)), [files]);
  const displayInitiator = !isReadOnly && (!files.length || mode === 'lean');

  const activeDropTarget = (
    <DropTarget
      acceptedContentTypes={acceptedContentTypes}
      placeholder={placeholder ?? ''}
      mode={mode}
      state="active"
    />
  );

  return (
    <View padding={1}>
      <MBFileDropListener
        onFilesDropped={handleDrop}
        fill
        overlay={activeDropTarget}
        disabled={isReadOnly}
      >
        {!isEmpty(files) && (
          <View gap={0.5}>
            <ImageList files={images} onFileRemoved={onFileRemoved} editable={!isReadOnly} />
            <FileList files={nonImages} onFileRemoved={onFileRemoved} editable={!isReadOnly} />
          </View>
        )}
        {displayInitiator && (
          <MBTouchableOverlay onPress={showFilePicker} fill overlay={activeDropTarget}>
            <DropTarget
              loading={uploading}
              placeholder={placeholder ?? ''}
              state={error ? 'error' : 'inactive'}
              mode={mode}
              acceptedContentTypes={acceptedContentTypes}
            />
          </MBTouchableOverlay>
        )}
      </MBFileDropListener>
    </View>
  );
}
